import { supabase } from "@/integrations/supabase/client";

export type ErrorType = 'database' | 'api' | 'edge_function' | 'auth' | 'storage';

export interface SystemError {
  error_type: ErrorType;
  error_code?: string;
  error_message: string;
  table_name?: string;
  operation?: string;
  request_context?: Record<string, any>;
  stack_trace?: string;
}

/**
 * Sanitize sensitive data from request context before logging
 */
const sanitizeContext = (context: Record<string, any>): Record<string, any> => {
  const sensitiveKeys = ['password', 'token', 'secret', 'key', 'authorization', 'credit_card', 'ssn'];
  const sanitized: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(context)) {
    const lowerKey = key.toLowerCase();
    if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeContext(value);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
};

/**
 * Log a system error to the database
 */
export const logSystemError = async (error: SystemError): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    const sanitizedContext = error.request_context 
      ? sanitizeContext(error.request_context) 
      : null;
    
    await supabase.from('system_error_log').insert({
      error_type: error.error_type,
      error_code: error.error_code || null,
      error_message: error.error_message,
      table_name: error.table_name || null,
      operation: error.operation || null,
      user_id: user?.id || null,
      request_context: sanitizedContext,
      stack_trace: error.stack_trace || null,
    });
  } catch (logError) {
    // Silently fail if logging fails to prevent cascade
    console.error('Failed to log system error:', logError);
  }
};

/**
 * Wrapper for safer Supabase operations with auto-logging
 */
export const safeQuery = async <T>(
  operation: () => Promise<{ data: T | null; error: any }>,
  context: { 
    table?: string; 
    operation?: 'select' | 'insert' | 'update' | 'delete' | 'rpc';
    payload?: Record<string, any>;
  }
): Promise<{ data: T | null; error: any }> => {
  try {
    const result = await operation();
    
    if (result.error) {
      await logSystemError({
        error_type: 'database',
        error_code: result.error.code,
        error_message: result.error.message || 'Unknown database error',
        table_name: context.table,
        operation: context.operation,
        request_context: context.payload ? { payload: context.payload } : undefined,
        stack_trace: new Error().stack,
      });
    }
    
    return result;
  } catch (unexpectedError: any) {
    await logSystemError({
      error_type: 'database',
      error_message: unexpectedError.message || 'Unexpected error during database operation',
      table_name: context.table,
      operation: context.operation,
      request_context: context.payload ? { payload: context.payload } : undefined,
      stack_trace: unexpectedError.stack,
    });
    
    return { data: null, error: unexpectedError };
  }
};

/**
 * Log API errors (for edge functions, external APIs)
 */
export const logApiError = async (
  endpoint: string,
  method: string,
  statusCode: number,
  errorMessage: string,
  requestBody?: any
): Promise<void> => {
  await logSystemError({
    error_type: 'api',
    error_code: statusCode.toString(),
    error_message: errorMessage,
    operation: method,
    request_context: {
      endpoint,
      method,
      statusCode,
      body: requestBody,
    },
  });
};

/**
 * Log authentication errors
 */
export const logAuthError = async (
  action: string,
  errorMessage: string,
  errorCode?: string
): Promise<void> => {
  await logSystemError({
    error_type: 'auth',
    error_code: errorCode,
    error_message: errorMessage,
    operation: action,
  });
};

/**
 * Log storage errors
 */
export const logStorageError = async (
  bucket: string,
  operation: string,
  errorMessage: string,
  filePath?: string
): Promise<void> => {
  await logSystemError({
    error_type: 'storage',
    error_message: errorMessage,
    operation,
    request_context: {
      bucket,
      filePath,
    },
  });
};

/**
 * Log edge function errors
 */
export const logEdgeFunctionError = async (
  functionName: string,
  errorMessage: string,
  statusCode?: number,
  payload?: any
): Promise<void> => {
  await logSystemError({
    error_type: 'edge_function',
    error_code: statusCode?.toString(),
    error_message: errorMessage,
    operation: functionName,
    request_context: payload ? { payload } : undefined,
  });
};
